import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Bot, CheckCircle2, LayoutDashboard, MessageSquareText, Sparkles } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const NEXBOT_APP_URL = 'https://www.bitrix24.es/apps/app/nextcoders.nexbot/';

const COPY = {
  en: {
    badge: 'Get started',
    title: 'Start with NexBot',
    intro:
      'NexBot is a self-service app. This page gives customers a clear starting point to review the product, legal information, and support options without relying on a marketplace redirect.',
    back: 'Back to NexBot',
    start: 'Open in Bitrix24',
    overview: 'View product page',
    includedTitle: 'What you can do with NexBot',
    included: [
      'How NexBot captures and classifies conversations inside Bitrix24',
      'How escalation to a human operator is configured',
      'How AI models, prompts, and channels are managed from the dashboard',
    ],
    flowTitle: 'Self-service setup flow',
    flow: [
      'Review the NexBot overview page',
      'Check the EULA and privacy policy',
      'Use the contact page if you need support or approval clarifications',
    ],
    readinessTitle: 'Built for self-service adoption',
    readinessBody:
      'Customers can understand the product, review legal details, and move forward with setup using a simple first-party flow on nextcoders.work.',
    linksTitle: 'Related links',
    privacy: 'Privacy policy',
    eula: 'EULA',
    contactPage: 'Contact page',
    overviewTitle: 'Why teams choose NexBot',
    overviewBody:
      'NexBot combines conversational automation, Bitrix24 integration, structured handoff rules, and centralized operational visibility in a single workflow.',
  },
  es: {
    badge: 'Comenzar',
    title: 'Empieza con NexBot',
    intro:
      'NexBot es una app self-service. Esta pagina ofrece un punto de inicio claro para revisar el producto, la informacion legal y las opciones de soporte sin depender de una redireccion al marketplace.',
    back: 'Volver a NexBot',
    start: 'Abrir en Bitrix24',
    overview: 'Ver pagina del producto',
    includedTitle: 'Que puedes hacer con NexBot',
    included: [
      'Como NexBot captura y clasifica conversaciones dentro de Bitrix24',
      'Como se configura la transferencia a un operador humano',
      'Como se gestionan modelos IA, prompts y canales desde el dashboard',
    ],
    flowTitle: 'Flujo de configuracion self-service',
    flow: [
      'Revisar la pagina principal de NexBot',
      'Revisar la EULA y la politica de privacidad',
      'Usar la pagina de contacto si necesitas soporte o aclaraciones para aprobacion',
    ],
    readinessTitle: 'Pensado para adopcion self-service',
    readinessBody:
      'Los clientes pueden entender el producto, revisar la informacion legal y avanzar con la configuracion usando un flujo simple dentro de nextcoders.work.',
    linksTitle: 'Enlaces relacionados',
    privacy: 'Politica de privacidad',
    eula: 'EULA',
    contactPage: 'Pagina de contacto',
    overviewTitle: 'Por que los equipos eligen NexBot',
    overviewBody:
      'NexBot combina automatizacion conversacional, integracion con Bitrix24, reglas estructuradas de transferencia y visibilidad operativa centralizada en un solo flujo.',
  },
  pt: {
    badge: 'Comecar',
    title: 'Comece com o NexBot',
    intro:
      'O NexBot e um app self-service. Esta pagina oferece um ponto de partida claro para revisar o produto, as informacoes legais e as opcoes de suporte sem depender de redirecionamento ao marketplace.',
    back: 'Voltar ao NexBot',
    start: 'Abrir no Bitrix24',
    overview: 'Ver pagina do produto',
    includedTitle: 'O que voce pode fazer com o NexBot',
    included: [
      'Como o NexBot captura e classifica conversas dentro do Bitrix24',
      'Como o encaminhamento para um operador humano e configurado',
      'Como modelos de IA, prompts e canais sao gerenciados no dashboard',
    ],
    flowTitle: 'Fluxo de configuracao self-service',
    flow: [
      'Revisar a pagina principal do NexBot',
      'Revisar a EULA e a politica de privacidade',
      'Usar a pagina de contato se precisar de suporte ou esclarecimentos para aprovacao',
    ],
    readinessTitle: 'Pensado para adocao self-service',
    readinessBody:
      'Os clientes podem entender o produto, revisar os detalhes legais e seguir com a configuracao usando um fluxo simples dentro de nextcoders.work.',
    linksTitle: 'Links relacionados',
    privacy: 'Politica de privacidade',
    eula: 'EULA',
    contactPage: 'Pagina de contato',
    overviewTitle: 'Por que as equipes escolhem o NexBot',
    overviewBody:
      'O NexBot combina automacao conversacional, integracao com Bitrix24, regras estruturadas de repasse e visibilidade operacional centralizada em um unico fluxo.',
  },
};

const getCopy = (language) => COPY[language] || COPY.es;

const PageHeader = () => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <Link to="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </Link>
      <div className="flex items-center gap-4 md:gap-6">
        <LanguageSwitcher variant="compact" />
        <Link to="/nexbot-contact" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          Contacto
        </Link>
        <Link to="/nexbot-eula" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          EULA
        </Link>
        <Link to="/nexbot-privacidad" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          Privacidad
        </Link>
      </div>
    </div>
  </header>
);

const ListCard = ({ icon: Icon, title, items }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-8">
    <div className="inline-flex items-center justify-center h-11 w-11 rounded-2xl border border-gray-200 bg-gray-50 text-gray-900 mb-4">
      <Icon size={18} />
    </div>
    <h2 className="text-xl font-bold text-[#141414]">{title}</h2>
    <div className="mt-5 space-y-3">
      {items.map((item) => (
        <div key={item} className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex items-start gap-3">
          <CheckCircle2 size={18} className="text-gray-900 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 leading-relaxed">{item}</div>
        </div>
      ))}
    </div>
  </div>
);

const NexbotDemoPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.language || 'es').split('-')[0];
  const copy = getCopy(lang);

  return (
    <div className="min-h-screen bg-transparent">
      <PageHeader />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/nexbot'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {copy.back}
          </button>

          <section className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_0.85fr] gap-10 items-start">
              <div>
                <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                  <Bot size={16} />
                  {copy.badge}
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-[#141414] leading-tight">{copy.title}</h1>
                <p className="mt-5 text-base md:text-lg text-gray-600 leading-relaxed">{copy.intro}</p>

                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <a
                    href={NEXBOT_APP_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex justify-center bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-xl transition"
                  >
                    {copy.start}
                  </a>
                  <Link
                    to="/nexbot"
                    className="inline-flex justify-center bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-xl transition"
                  >
                    {copy.overview}
                  </Link>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-3xl p-6">
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-white border border-gray-200 text-gray-900">
                  <Sparkles size={20} />
                </div>
                <h2 className="mt-5 text-xl font-bold text-[#141414]">{copy.overviewTitle}</h2>
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{copy.overviewBody}</p>

                <div className="mt-6 grid gap-3">
                  {[
                    'Bitrix24',
                    'AI routing',
                    'Human handoff',
                    'Dashboard control',
                  ].map((item) => (
                    <div key={item} className="rounded-2xl border border-white bg-white px-4 py-3 text-sm font-medium text-[#141414]">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <ListCard icon={LayoutDashboard} title={copy.includedTitle} items={copy.included} />
            <ListCard icon={MessageSquareText} title={copy.flowTitle} items={copy.flow} />
          </section>

          <section className="mt-8 bg-white border border-gray-200 rounded-3xl p-8 md:p-10">
            <h2 className="text-2xl font-bold text-[#141414]">{copy.readinessTitle}</h2>
            <p className="mt-3 text-gray-600 leading-relaxed max-w-3xl">{copy.readinessBody}</p>

            <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="text-sm font-semibold text-[#141414]">{copy.linksTitle}</div>
              <div className="flex flex-wrap gap-4 text-sm">
                <Link to="/nexbot-contact" className="text-[#141414] hover:underline">{copy.contactPage}</Link>
                <Link to="/nexbot-eula" className="text-[#141414] hover:underline">{copy.eula}</Link>
                <Link to="/nexbot-privacidad" className="text-[#141414] hover:underline">{copy.privacy}</Link>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default NexbotDemoPage;
